/**
 * SubscriptionOverrideManager Component
 *
 * Allows super admins to override an organization's subscription tier,
 * status, and expiration dates.
 */

import { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  CircularProgress,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { addYears, addMonths, addDays } from 'date-fns';
import {
  adminSubscriptionApi,
  SubscriptionTier,
  SubscriptionStatus,
  OrganizationSubscription,
} from '../../services/admin';

interface SubscriptionOverrideManagerProps {
  orgId: string;
  orgName: string;
  currentSubscription?: OrganizationSubscription;
  onUpdate?: () => void;
}

const TIER_OPTIONS: { value: SubscriptionTier; label: string; color: string }[] = [
  { value: 'trial', label: 'Trial', color: '#9e9e9e' },
  { value: 'launch', label: 'Launch', color: '#4caf50' },
  { value: 'rise', label: 'Rise', color: '#2196f3' },
  { value: 'summit', label: 'Summit', color: '#9c27b0' },
  { value: 'expired', label: 'Expired', color: '#f44336' },
];

const STATUS_OPTIONS: { value: SubscriptionStatus; label: string }[] = [
  { value: 'trialing', label: 'Trialing' },
  { value: 'active', label: 'Active' },
  { value: 'past_due', label: 'Past Due' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'expired', label: 'Expired' },
];

const QUICK_DURATIONS = [
  { label: '30 Days', getValue: () => addDays(new Date(), 30) },
  { label: '90 Days', getValue: () => addDays(new Date(), 90) },
  { label: '6 Months', getValue: () => addMonths(new Date(), 6) },
  { label: '1 Year', getValue: () => addYears(new Date(), 1) },
  { label: '5 Years', getValue: () => addYears(new Date(), 5) },
  { label: '10 Years', getValue: () => addYears(new Date(), 10) },
];

export function SubscriptionOverrideManager({
  orgId,
  orgName,
  currentSubscription,
  onUpdate,
}: SubscriptionOverrideManagerProps) {
  const [tier, setTier] = useState<SubscriptionTier>(currentSubscription?.tier || 'trial');
  const [status, setStatus] = useState<SubscriptionStatus>(
    (currentSubscription?.status as SubscriptionStatus) || 'trialing'
  );
  const [expirationDate, setExpirationDate] = useState<Date | null>(
    currentSubscription?.currentPeriodEnd ? new Date(currentSubscription.currentPeriodEnd) : null
  );
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleQuickDuration = (getValue: () => Date) => {
    setExpirationDate(getValue());
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await adminSubscriptionApi.override(orgId, {
        tier,
        status,
        currentPeriodEnd: expirationDate?.toISOString(),
        notes: notes || `Manual override: Set to ${tier} tier`,
      });

      setSuccess(`Successfully updated ${orgName} to ${tier} tier`);
      setConfirmOpen(false);
      onUpdate?.();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update subscription';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getTierColor = (t: SubscriptionTier) => {
    return TIER_OPTIONS.find((opt) => opt.value === t)?.color || '#9e9e9e';
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          Subscription Override
          {currentSubscription?.adminOverride && (
            <Chip
              label="Previously Overridden"
              size="small"
              color="warning"
              variant="outlined"
            />
          )}
        </Typography>

        {/* Current Status Display */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Current Status
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Chip
              label={currentSubscription?.tier || 'Unknown'}
              size="small"
              sx={{ bgcolor: getTierColor(currentSubscription?.tier || 'trial'), color: 'white' }}
            />
            <Typography variant="body2">
              Status: {currentSubscription?.status || 'Unknown'}
            </Typography>
            {currentSubscription?.currentPeriodEnd && (
              <Typography variant="body2">
                Expires: {new Date(currentSubscription.currentPeriodEnd).toLocaleDateString()}
              </Typography>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Tier Selection */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Tier</InputLabel>
          <Select
            value={tier}
            label="Tier"
            onChange={(e) => setTier(e.target.value as SubscriptionTier)}
          >
            {TIER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: option.color,
                    }}
                  />
                  {option.label}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Status Selection */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={status}
            label="Status"
            onChange={(e) => setStatus(e.target.value as SubscriptionStatus)}
          >
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Expiration Date */}
        <Box sx={{ mb: 2 }}>
          <DatePicker
            label="Expiration Date"
            value={expirationDate}
            onChange={(newValue) => setExpirationDate(newValue)}
            slotProps={{
              textField: { fullWidth: true, size: 'medium' },
            }}
          />
        </Box>

        {/* Quick Duration Buttons */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Quick Set Expiration:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {QUICK_DURATIONS.map((duration) => (
              <Button
                key={duration.label}
                size="small"
                variant="outlined"
                onClick={() => handleQuickDuration(duration.getValue)}
              >
                {duration.label}
              </Button>
            ))}
          </Box>
        </Box>

        {/* Notes */}
        <TextField
          fullWidth
          label="Notes (optional)"
          placeholder="Reason for override..."
          multiline
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* Submit Button */}
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={() => setConfirmOpen(true)}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Apply Override'}
        </Button>

        {/* Confirmation Dialog */}
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
          <DialogTitle>Confirm Subscription Override</DialogTitle>
          <DialogContent>
            <DialogContentText>
              You are about to override the subscription for <strong>{orgName}</strong>:
            </DialogContentText>
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Tier:</strong> {tier}
              </Typography>
              <Typography variant="body2">
                <strong>Status:</strong> {status}
              </Typography>
              {expirationDate && (
                <Typography variant="body2">
                  <strong>Expires:</strong> {expirationDate.toLocaleDateString()}
                </Typography>
              )}
              {notes && (
                <Typography variant="body2">
                  <strong>Notes:</strong> {notes}
                </Typography>
              )}
            </Box>
            <DialogContentText sx={{ mt: 2, color: 'warning.main' }}>
              This will bypass Stripe billing. The user will have access to the selected tier
              until the expiration date without needing to pay.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained" color="primary" disabled={loading}>
              {loading ? <CircularProgress size={20} /> : 'Confirm Override'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
}
